import ComparePage, { CompareRow, CompareFaq, Reason } from '@comp/compare/compare-template'
import AssetDownload from '@comp/product/asset-download'

interface PageContent {
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  reasons: Reason[]
  tableTitle: [string, string]
  tableBody: string
  rows: CompareRow[]
  faqs: CompareFaq[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    seoTitle: 'Venafi Alternative | Monofor | Certificate lifecycle inside PAM',
    seoDescription:
      'Looking for a Venafi alternative? Monopam Certificate Manager delivers agentless discovery, ACME and AD CS automation, a built-in private CA, and renew-and-deploy, inside the PAM platform, not another silo.',
    heroEyebrow: 'Venafi alternative',
    heroTitle: ['Certificate lifecycle,', 'without another platform.'],
    heroBody:
      'Venafi defined enterprise certificate management: as a separate, heavyweight platform with enterprise pricing to match. With public certificate lifetimes heading toward 47 days by 2029, every team needs certificate automation now, not just the Fortune 500. Monopam Certificate Manager puts agentless discovery, automated renewal, and deployment inside the PAM platform you already run, deployed in minutes.',
    reasons: [
      {
        title: '47 days changes who needs CLM',
        body: 'The CA/Browser Forum has set public TLS certificate lifetimes on a path down to 47 days by 2029. At that cadence, manual tracking and renewal stops being risky and becomes impossible; certificate automation turns from a Fortune 500 purchase into table stakes for every team.',
      },
      {
        title: 'Inside the platform, not beside it',
        body: 'A dedicated CLM suite means another console, another integration project, another contract to renew. In Monopam, certificates live next to the vault, sessions, and approvals you already operate: one platform, one console, one bill.',
      },
      {
        title: 'A closed loop, not a spreadsheet',
        body: 'Discovery finds certificates by network scan and in server stores; renewal runs through ACME, AD CS, or the built-in CA; deployment pushes the renewed certificate to nginx, Apache, or IIS and verifies it is serving. Expiry alerts and CT-log monitoring watch the edges.',
      },
    ],
    tableTitle: ['Monofor vs Venafi,', 'feature by feature.'],
    tableBody:
      'On core certificate lifecycle the platforms overlap; Venafi has been doing this longest. The difference is footprint: a module inside the PAM platform you already run, versus a dedicated enterprise suite bought and integrated on its own.',
    rows: [
      {
        feature: 'Agentless certificate discovery',
        detail: 'Network scan + server certificate stores via gateway',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'ACME support',
        detail: 'Let’s Encrypt and any ACMEv2-compatible CA',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Microsoft AD CS integration',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Built-in private CA',
        detail: 'Internal certificates without extra PKI infrastructure',
        monofor: true,
        competitor: 'Separate service',
      },
      {
        feature: 'Renew-and-deploy automation',
        detail: 'nginx, Apache, IIS: deploy and verify',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Expiry alerting & dashboards',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'CT-log monitoring',
        detail: 'Spot rogue or unexpected certificates for your domains',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'PQC / crypto-agility reporting',
        detail: 'Inventory of algorithms, key sizes, weak crypto',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Part of a PAM platform',
        detail: 'Vault, session recording, JIT in the same product',
        monofor: true,
        competitor: 'Separate platform',
      },
      {
        feature: 'IAM + IGA in the same suite',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Deploy in minutes without professional services',
        monofor: true,
        competitor: 'PS engagement typical',
      },
      {
        feature: 'Priced for teams of every size',
        monofor: true,
        competitor: 'Enterprise quote-based',
      },
    ],
    faqs: [
      {
        q: 'Do we need a separate CLM tool at all?',
        a: 'Not with Monopam. Certificate Manager is a module of the PAM platform: discovery, issuance, renewal, deployment, alerting, and reporting run in the same console as your vault and privileged sessions. No second platform to buy, integrate, and renew.',
      },
      {
        q: 'Which certificate authorities are supported?',
        a: 'ACME (including Let’s Encrypt and any ACMEv2-compatible CA), Microsoft AD CS for Windows-centric PKI, and a built-in private CA for internal certificates. Public, enterprise, and internal issuance all run through the same lifecycle.',
      },
      {
        q: 'Is discovery really agentless?',
        a: 'Yes. Certificates are discovered by network scanning and by reading server certificate stores over the gateway using SSH and WinRM; no agent rollout on your fleet.',
      },
      {
        q: 'Does it actually deploy the renewed certificate, or just alert us?',
        a: 'It closes the loop. Renewed certificates are deployed to nginx, Apache, and IIS through the gateway, the service is reloaded, and the deployment is verified, with alerting as the safety net rather than the whole product.',
      },
      {
        q: 'Why does the 47-day lifetime matter for us?',
        a: 'Public TLS certificate lifetimes are on an industry-agreed schedule stepping down to 47 days by 2029. A certificate estate that renews eight times a year per certificate cannot be managed by spreadsheet and calendar reminders; automation becomes the only viable operating model.',
      },
    ],
  },
  tr: {
    seoTitle: 'Venafi Alternatifi | Monofor | PAM içinde sertifika yaşam döngüsü',
    seoDescription:
      'Venafi alternatifi mi arıyorsunuz? Monopam Sertifika Yöneticisi; ajansız keşif, ACME ve AD CS otomasyonu, yerleşik özel CA ve yenile-ve-dağıt döngüsünü, ayrı bir silo değil, PAM platformunun içinde sunar.',
    heroEyebrow: 'Venafi alternatifi',
    heroTitle: ['Sertifika yaşam döngüsü,', 'ayrı bir platform olmadan.'],
    heroBody:
      'Venafi kurumsal sertifika yönetimini tanımladı: ayrı, ağır bir platform ve ona uygun kurumsal fiyatlandırmayla. Halka açık sertifika ömürleri 2029’a doğru 47 güne inerken artık her ekibin sertifika otomasyonuna ihtiyacı var, yalnızca Fortune 500’ün değil. Monopam Sertifika Yöneticisi; ajansız keşfi, otomatik yenilemeyi ve dağıtımı zaten işlettiğiniz PAM platformunun içine koyar; dakikalar içinde kurulur.',
    reasons: [
      {
        title: '47 gün, CLM’e kimin ihtiyacı olduğunu değiştiriyor',
        body: 'CA/Browser Forum, halka açık TLS sertifika ömürlerini 2029’a kadar 47 güne inen bir takvime bağladı. Bu tempoda elle takip ve yenileme riskli olmaktan çıkıp imkânsız hale gelir; sertifika otomasyonu Fortune 500’e özgü bir satın almadan her ekip için asgari standarda dönüşür.',
      },
      {
        title: 'Platformun yanında değil, içinde',
        body: 'Ayrı bir CLM paketi; bir konsol daha, bir entegrasyon projesi daha, yenilenecek bir sözleşme daha demektir. Monopam’de sertifikalar, zaten işlettiğiniz kasanın, oturumların ve onayların yanında yaşar: tek platform, tek konsol, tek fatura.',
      },
      {
        title: 'Tablo değil, kapalı döngü',
        body: 'Keşif; sertifikaları ağ taramasıyla ve sunucu depolarında bulur. Yenileme; ACME, AD CS veya yerleşik CA üzerinden çalışır. Dağıtım; yenilenen sertifikayı nginx, Apache veya IIS’e iter ve sunulduğunu doğrular. Sona erme uyarıları ve CT-log izleme de kenarları gözler.',
      },
    ],
    tableTitle: ['Monofor ve Venafi,', 'özellik özellik.'],
    tableBody:
      'Çekirdek sertifika yaşam döngüsünde platformlar örtüşür; bu işi en uzun süredir yapan Venafi. Fark ayak izinde: zaten işlettiğiniz PAM platformunun içinde bir modül mü, yoksa kendi başına satın alınıp entegre edilen ayrı bir kurumsal paket mi.',
    rows: [
      {
        feature: 'Ajansız sertifika keşfi',
        detail: 'Ağ geçidi üzerinden ağ taraması + sunucu sertifika depoları',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'ACME desteği',
        detail: 'Let’s Encrypt ve ACMEv2 uyumlu her CA',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Microsoft AD CS entegrasyonu',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Yerleşik özel CA',
        detail: 'Ek PKI altyapısı olmadan iç sertifikalar',
        monofor: true,
        competitor: 'Ayrı hizmet',
      },
      {
        feature: 'Yenile-ve-dağıt otomasyonu',
        detail: 'nginx, Apache, IIS: dağıt ve doğrula',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Sona erme uyarıları ve panolar',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'CT-log izleme',
        detail: 'Alan adlarınız için sahte veya beklenmedik sertifikaları yakalayın',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'PQC / kripto-çeviklik raporlaması',
        detail: 'Algoritma, anahtar boyu ve zayıf kripto envanteri',
        monofor: true,
        competitor: true,
      },
      {
        feature: 'Bir PAM platformunun parçası',
        detail: 'Kasa, oturum kaydı, JIT aynı üründe',
        monofor: true,
        competitor: 'Ayrı platform',
      },
      {
        feature: 'IAM + IGA aynı pakette',
        monofor: true,
        competitor: false,
      },
      {
        feature: 'Profesyonel hizmet olmadan dakikalar içinde kurulum',
        monofor: true,
        competitor: 'Genellikle PS projesi gerekir',
      },
      {
        feature: 'Her ölçekte ekip için fiyatlandırma',
        monofor: true,
        competitor: 'Kurumsal, teklife dayalı',
      },
    ],
    faqs: [
      {
        q: 'Ayrı bir CLM aracına gerçekten ihtiyacımız var mı?',
        a: 'Monopam ile hayır. Sertifika Yöneticisi, PAM platformunun bir modülüdür: keşif, düzenleme, yenileme, dağıtım, uyarı ve raporlama; kasanız ve ayrıcalıklı oturumlarınızla aynı konsolda çalışır. Satın alınacak, entegre edilecek ve yenilenecek ikinci bir platform yok.',
      },
      {
        q: 'Hangi sertifika otoriteleri destekleniyor?',
        a: 'ACME (Let’s Encrypt ve ACMEv2 uyumlu her CA dahil), Windows odaklı PKI için Microsoft AD CS ve iç sertifikalar için yerleşik özel CA. Halka açık, kurumsal ve iç sertifika düzenleme aynı yaşam döngüsünden geçer.',
      },
      {
        q: 'Keşif gerçekten ajansız mı?',
        a: 'Evet. Sertifikalar; ağ taramasıyla ve ağ geçidi üzerinden SSH ve WinRM kullanılarak sunucu sertifika depolarının okunmasıyla keşfedilir; filonuza ajan dağıtımı gerekmez.',
      },
      {
        q: 'Yenilenen sertifikayı gerçekten dağıtıyor mu, yoksa yalnızca uyarı mı veriyor?',
        a: 'Döngüyü kapatır. Yenilenen sertifikalar ağ geçidi üzerinden nginx, Apache ve IIS’e dağıtılır, servis yeniden yüklenir ve dağıtım doğrulanır; uyarı, ürünün tamamı değil emniyet ağıdır.',
      },
      {
        q: '47 günlük ömür bizim için neden önemli?',
        a: 'Halka açık TLS sertifika ömürleri, sektörün üzerinde anlaştığı bir takvimle 2029’a kadar kademeli olarak 47 güne iniyor. Sertifika başına yılda sekiz kez yenilenen bir envanter; tablolar ve takvim hatırlatıcılarıyla yönetilemez; otomasyon tek uygulanabilir işletim modeli haline gelir.',
      },
    ],
  },
}

type Props = { locale: string }

export default function VenafiAlternativePage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <ComparePage
      leadMagnet={
        <AssetDownload
          asset="clm-whitepaper"
          title={{
            en: 'Go deeper: the CLM whitepaper.',
            tr: 'Derine inin: CLM whitepaper’ı.',
          }}
          copy={{
            en: 'Why lifetimes are collapsing to 47 days and how PAM-native certificate lifecycle management answers it: 11 pages with live product screenshots.',
            tr: 'Sertifika ömürleri neden 47 güne iniyor ve PAM-yerlisi CLM buna nasıl cevap veriyor: canlı ürün ekran görüntüleriyle 11 sayfa.',
          }}
        />
      }
      competitor="Venafi"
      locale={locale}
      bilingual
      path="/compare/venafi-alternative"
      seoTitle={c.seoTitle}
      seoDescription={c.seoDescription}
      heroEyebrow={c.heroEyebrow}
      heroTitle={
        <>
          {c.heroTitle[0]}
          <br />
          {c.heroTitle[1]}
        </>
      }
      heroBody={c.heroBody}
      reasons={c.reasons}
      tableTitle={
        <>
          {c.tableTitle[0]}
          <br />
          {c.tableTitle[1]}
        </>
      }
      tableBody={c.tableBody}
      rows={c.rows}
      faqs={c.faqs}
    />
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
